import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input, Select, Button, Table, Space, Card, message, Spin, Typography } from 'antd';
import { Play, Save, Settings, Book } from 'lucide-react';
import MonacoEditor from '@monaco-editor/react';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import VisualizationConfigModal from '../../../shared/components/modals/VisualizationConfigModal';
import EnhancedVisualization from '../../../shared/components/visualization/EnhancedVisualization';
import SaveQueryModal from '../../../shared/components/modals/SaveQueryModal';
// Saved queries list moved to dedicated Queries list page
import suppressResizeObserverErrors from '../../../shared/utils/resizeObserverFix';

// Apply ResizeObserver error suppression
suppressResizeObserverErrors();

const { Title, Text } = Typography;

// Updated Design System styles - Matching other dashboard pages
const styles = {
  primaryBackground: "#ffffff", // White
  secondaryBackground: "#DDE2EE", // Light blue-grey
  textPrimary: "#1d0c46", // Brand color for primary text
  textSecondary: "#666666", // Grey for secondary text
  footerBackground: "#000000", // Black background
  footerText: "#ffffff", // White text for footer
  borderColor: "#E5E7EB", // Gray border (matching border-gray-200)
  cardBg: "#ffffff", // White card background
  cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)",
  // Typography
  headingFont: "Raleway, sans-serif",
  bodyFont: "Raleway, sans-serif"
};

const brandColor = '#1d0c46';

const QueryStudioContent = () => {
  
  // State management
  const [queryName, setQueryName] = useState('');
  const [visualizationType, setVisualizationType] = useState('Table');
  const [sqlQuery, setSqlQuery] = useState('-- Write your SQL query here\n-- Example queries:\nSELECT * FROM sessions LIMIT 10;\nSELECT * FROM analytics LIMIT 5;\nSELECT sessionId, userId, duration FROM sessions LIMIT 10;');
  const [editorMounted, setEditorMounted] = useState(false);
  const [queryResults, setQueryResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [loadingQuery, setLoadingQuery] = useState(false);
  // Require running Table once before unlocking other visualizations
  const [hasTableRun, setHasTableRun] = useState(false);
  // Track the last executed SQL to detect new queries
  const [lastExecutedSql, setLastExecutedSql] = useState('');
  // Saved queries are no longer shown here; fetched only when loading a specific query
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Track current query ID when editing an existing query
  const [currentQueryId, setCurrentQueryId] = useState(null);
  
  // Visualization configuration state
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [visualizationConfigs, setVisualizationConfigs] = useState({});
  
  // Save query modal state
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  
  // Batch query navigation state
  const [currentBatchQueryIndex, setCurrentBatchQueryIndex] = useState(0);

  // Guards to avoid duplicate auto actions (StrictMode double-invokes effects in dev)
  const hasAutoLoadedRef = useRef(false);
  const hasAutoExecutedRef = useRef(false);


  // Debounced resize handler to prevent ResizeObserver errors
  const [resizeTimeout, setResizeTimeout] = useState(null);
  
  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      setResizeTimeout(setTimeout(() => {
        // Force a re-render to stabilize layout
        window.dispatchEvent(new Event('resize'));
      }, 100));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [resizeTimeout]);

  // Load visualization configurations from localStorage
  useEffect(() => {
    const savedConfigs = localStorage.getItem('visualizationConfigs');
    if (savedConfigs) {
      try {
        setVisualizationConfigs(JSON.parse(savedConfigs));
      } catch (error) {
        console.error('Error loading visualization configs:', error);
      }
    }
  }, []);

  // Save visualization configurations to localStorage
  const saveVisualizationConfig = (config) => {
    const newConfigs = {
      ...visualizationConfigs,
      [visualizationType]: config
    };
    setVisualizationConfigs(newConfigs);
    localStorage.setItem('visualizationConfigs', JSON.stringify(newConfigs));
  };

  // Get current visualization configuration
  const getCurrentConfig = () => {
    return visualizationConfigs[visualizationType] || {};
  };

  // Reset table configuration to default values
  const resetTableConfigurationToDefaults = () => {
    if (queryResults && queryResults.columns && queryResults.columns.length > 0) {
      const defaultTableConfig = {
        tableName: '',
        columns: queryResults.columns.map(col => ({ key: col, label: col, color: undefined, visible: true })),
        headerColor: '#ffffff',
        headerTextcolor: '#0f172a',
        zebraStripes: false,
        pageSize: 10
      };
      
      const newConfigs = {
        ...visualizationConfigs,
        'Table': defaultTableConfig
      };
      setVisualizationConfigs(newConfigs);
      localStorage.setItem('visualizationConfigs', JSON.stringify(newConfigs));
    }
  };

  // Check if configuration is available for current visualization type
  const isConfigAvailable = () => {
    return ['Line Chart', 'Bar Chart', 'Pie Chart', 'KPI'].includes(visualizationType);
  };

  // Check if batch query has multiple results (prevents configuration)
  const isBatchWithMultipleResults = () => {
    return queryResults && queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries.length > 1;
  };

  // Check if table configuration is available (only after query execution)
  const isTableConfigAvailable = () => {
    return visualizationType === 'Table' && queryResults && queryResults.columns && queryResults.columns.length > 0 && !isBatchWithMultipleResults();
  };

  // Handle visualization type change
  const handleVisualizationTypeChange = (newType) => {
    if (isBatchWithMultipleResults()) {
      message.warning('Cannot change visualization type for batch queries with multiple tables. Please select a single table first.');
      return;
    }
    setVisualizationType(newType);
    // Auto-open config modal for non-table visualizations if results exist
    if (newType !== 'Table' && queryResults && isConfigAvailable()) {
      setTimeout(() => setConfigModalVisible(true), 100);
    }
  };

  // Visualization types (full list; UI will reveal selector only after table has run)
  const visualizationOptions = [
    { value: 'Table', label: 'Table' },
    { value: 'Line Chart', label: 'Line Chart' },
    { value: 'Bar Chart', label: 'Bar Chart' },
    { value: 'KPI', label: 'KPI' },
    { value: 'Pie Chart', label: 'Pie Chart' }
  ];

  // Check authentication and load query if queryId is present in URL params
  useEffect(() => {
    // Check authentication first
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) {
      setIsAuthenticated(false);
      message.warning('Please log in to use Query Studio');
      return;
    }
    
    setIsAuthenticated(true);
    // If a queryId is present in URL params, load it
    const params = new URLSearchParams(window.location.search);
    const queryId = params.get('queryId');
    const shouldRun = params.get('run') === 'true';
    if (queryId && !hasAutoLoadedRef.current) {
      hasAutoLoadedRef.current = true;
      loadQueryById(queryId, shouldRun, true);
    }
    
  }, []);

  // Monitor MonacoEditor mounting with visibility awareness
  useEffect(() => {
    const container = document.querySelector('.sql-editor-container');
    const isVisible = () => {
      if (!container) return false;
      const rect = container.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const editorTimeout = setTimeout(() => {
      if (!editorMounted && isVisible()) {
        console.warn('MonacoEditor failed to mount within 8 seconds, fallback textarea will be shown');
      }
    }, 8000);

    return () => clearTimeout(editorTimeout);
  }, [editorMounted]);

  // When the SQL text differs from the last executed SQL, treat it as a new query
  useEffect(() => {
    if (hasTableRun && sqlQuery !== lastExecutedSql) {
      setHasTableRun(false);
      setVisualizationType('Table');
      setConfigModalVisible(false);
      // Clear currentQueryId when SQL changes to treat as new query
      setCurrentQueryId(null);
    }
  }, [sqlQuery, lastExecutedSql, hasTableRun]);


  // Load a specific saved query by ID
  const loadQueryById = useCallback(async (id, autoRun = false, showToast = true) => {
    try {
      setLoadingQuery(true);
      
      // Get the currently selected team ID
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData._id;
      
      if (!teamId) {
        message.error('Team ID not found. Please select a team.');
        setLoadingQuery(false);
        return;
      }
      
      const res = await axiosInstance.get(`/query/${id}?teamId=${teamId}`);
      const q = res.data.query || res.data;
      if (q) {
        setQueryName(q.name || '');
        setSqlQuery(q.sqlQuery || '');
        setVisualizationType(q.visualizationType || 'Table');
        // Track the current query ID for updates
        setCurrentQueryId(q._id || id);
        
        // FIX: If saved query has non-Table visualization type, it was already validated
        // Set hasTableRun to true to allow immediate execution
        if (q.visualizationType && q.visualizationType !== 'Table') {
          setHasTableRun(true);
        } else {
          setHasTableRun(false);
        }
        
        setLastExecutedSql('');
        if (q.visualizationConfig) {
          setVisualizationConfigs(prev => ({ ...prev, [q.visualizationType || 'Table']: q.visualizationConfig }));
          // Also update localStorage to persist the loaded configuration
          const newConfigs = {
            ...visualizationConfigs,
            [q.visualizationType || 'Table']: q.visualizationConfig
          };
          localStorage.setItem('visualizationConfigs', JSON.stringify(newConfigs));
        }
        if (showToast) {
          message.success(`Loaded query: ${q.name}`);
        }
        if (autoRun && !hasAutoExecutedRef.current) {
          hasAutoExecutedRef.current = true;
          setTimeout(() => executeQuery(q.sqlQuery || '', q._id, true, false), 0);
        }
      }
    } catch (error) {
      console.error('Failed to load query by id', error);
      message.error('Failed to load query');
    } finally {
      setLoadingQuery(false);
    }
  }, []);


  // Execute query
  const executeQuery = async (overrideSql, overrideQueryId, showToast = true, isNewQuery = true) => {
    const queryText = typeof overrideSql === 'string' ? overrideSql : sqlQuery;
    if (!queryText.trim()) {
      message.error('Please enter a SQL query');
      return;
    }
    
    // FIX: Allow saved queries (with overrideQueryId) to bypass the Table requirement
    // Also allow if it's a Table visualization type
    const isSavedQuery = !!overrideQueryId;
    const shouldBypassGate = isSavedQuery || visualizationType === 'Table';
    
    // Enforce that Table must run before other visualizations (only for new queries)
    if (!hasTableRun && visualizationType !== 'Table' && !shouldBypassGate) {
      message.warning('Please run the Table visualization first to unlock other types.');
      return;
    }

    try {
      setExecuting(true);
      // Get the currently selected team ID
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const currentTeamId = selectedTeamData._id || localStorage.getItem("selectedTeam");
      
      const response = await axiosInstance.post('/query/execute', {
        query: queryText,
        queryId: overrideQueryId,
        teamId: currentTeamId
      });
      
      let newQueryResults;
      
      // Check if this is a batch execution (multiple queries)
      if (response.data.batchExecution) {
        // Handle multiple query results
        const batchData = response.data;
        
        if (batchData.queries && batchData.queries.length > 0) {
          // Process all successful queries with results
          const successfulQueries = batchData.queries.filter(q => q.success && q.data && q.data.length > 0);
          
          if (successfulQueries.length > 0) {
            // For batch execution, store all query results
            newQueryResults = {
              isBatch: true,
              batchQueries: successfulQueries.map(query => ({
                queryIndex: query.queryIndex,
                query: query.query,
                data: query.data,
                columns: query.data.length > 0 ? Object.keys(query.data[0]) : [],
                count: query.count || query.data.length,
                executionTime: query.executionTime
              })),
              // For backward compatibility, use first query's data
              data: successfulQueries[0].data,
              columns: successfulQueries[0].data.length > 0 ? Object.keys(successfulQueries[0].data[0]) : [],
              batchInfo: {
                totalQueries: batchData.totalQueries,
                successfulQueries: batchData.successfulQueries,
                failedQueries: batchData.failedQueries,
                hasErrors: batchData.hasErrors,
                queriesWithResults: successfulQueries.length
              }
            };
            
            // Show batch execution summary
            if (showToast) {
              if (batchData.hasErrors) {
                message.warning(`Batch executed: ${batchData.successfulQueries}/${batchData.totalQueries} queries successful, ${successfulQueries.length} with results`);
              } else {
                message.success(`All ${batchData.totalQueries} queries executed successfully, ${successfulQueries.length} with results`);
              }
            }
          } else {
            // No successful queries with results
            newQueryResults = {
              data: [],
              columns: [],
              batchInfo: {
                totalQueries: batchData.totalQueries,
                successfulQueries: batchData.successfulQueries,
                failedQueries: batchData.failedQueries,
                hasErrors: batchData.hasErrors,
                message: 'No queries returned results'
              }
            };
            
            if (showToast) {
              message.warning(`Batch executed but no results returned. ${batchData.successfulQueries}/${batchData.totalQueries} queries successful`);
            }
          }
        } else {
          // Empty batch results
          newQueryResults = {
            data: [],
            columns: [],
            batchInfo: {
              message: 'No query results available'
            }
          };
          
          if (showToast) {
            message.warning('Queries executed but no results returned');
          }
        }
      } else {
        // Handle single query results (backward compatible)
        newQueryResults = {
          data: response.data.data || [],
          columns: response.data.columns || []
        };
      }
      
      setQueryResults(newQueryResults);
      
      // Reset batch query index when new results are set
      setCurrentBatchQueryIndex(0);
      
      // Mark Table as run to unlock other visualizations
      // FIX: For saved queries with non-Table types, we already set hasTableRun during load
      // But still mark it here if executing as Table
      if (visualizationType === 'Table' && newQueryResults.columns.length >= 0) {
        if (!hasTableRun) {
          setHasTableRun(true);
        }
      } else if (overrideQueryId && visualizationType !== 'Table') {
        // For saved queries with non-Table visualization types, ensure hasTableRun is true
        if (!hasTableRun) {
          setHasTableRun(true);
        }
      }
      
      // Reset visualization configuration to defaults only for new query execution (not when loading saved queries)
      if (isNewQuery) {
        if (visualizationType === 'Table') {
          // Build fresh table defaults from returned columns
          const defaultTableConfig = {
            tableName: '',
            columns: newQueryResults.columns.map(col => ({ key: col, label: col, color: undefined, visible: true })),
            headerColor: '#ffffff',
            headerTextcolor: '#0f172a',
            zebraStripes: false,
            pageSize: 10
          };
          const newConfigs = {
            ...visualizationConfigs,
            Table: defaultTableConfig
          };
          setVisualizationConfigs(newConfigs);
          localStorage.setItem('visualizationConfigs', JSON.stringify(newConfigs));
        } else {
          // For non-table visualizations, clear current type config to use component defaults
          const newConfigs = { ...visualizationConfigs };
          newConfigs[visualizationType] = {};
          setVisualizationConfigs(newConfigs);
          localStorage.setItem('visualizationConfigs', JSON.stringify(newConfigs));
        }
      }
      
      // Only show success message for single queries (batch messages are handled above)
      if (showToast && !response.data.batchExecution) {
        message.success('Query executed successfully');
      }
      // Update lastExecutedSql after success
      setLastExecutedSql(queryText);
    } catch (error) {
      console.error('Error executing query:', error);
      message.error(error.response?.data?.message || 'Failed to execute query');
      setQueryResults(null);
    } finally {
      setExecuting(false);
    }
  };

  // Save query
  const handleSaveQuery = async (values) => {
    if (!sqlQuery.trim()) {
      message.error('Please enter a SQL query');
      return;
    }

    // Check if this is a batch query with multiple results
    if (queryResults && queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries.length > 1) {
      message.warning('Cannot save batch queries with multiple tables. Please select a single table to save, or modify your query to return only one result set.');
      return;
    }

    try {
      setSaving(true);
      
      // Get the currently selected team ID
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData._id;
      
      if (!teamId) {
        message.error('Team ID not found. Please select a team.');
        setSaving(false);
        return;
      }
      
      const queryData = {
        name: values.queryName,
        description: values.description || '',
        sqlQuery: sqlQuery,
        visualizationType: visualizationType,
        visualizationConfig: getCurrentConfig(),
        teamId: teamId // Include the currently selected team ID
      };

      // If we're editing an existing query, use PUT to update it
      if (currentQueryId) {
        const updateData = {
          name: values.queryName,
          description: values.description || '',
          query: sqlQuery,
          visualizationType: visualizationType,
          visualizationConfig: getCurrentConfig(),
          teamId: teamId // Include the currently selected team ID
        };
        console.log('Updating query:', { currentQueryId, visualizationType, updateData });
        await axiosInstance.put(`/query/${currentQueryId}`, updateData);
        message.success('Query updated successfully');
        setSaveModalVisible(false);
        return;
      }

      // Otherwise, try to save as new query
      const response = await axiosInstance.post('/query/save', queryData);
      // Track the newly created query ID for future updates
      if (response.data?.query?._id) {
        setCurrentQueryId(response.data.query._id);
      }
      message.success('Query saved successfully');
      setSaveModalVisible(false);
    } catch (error) {
      console.error('Error saving query:', error);
      // If a query with the same name already exists, update it instead
      const status = error?.response?.status;
      const existingId = error?.response?.data?.existingQueryId;
      if (status === 409 && existingId) {
        try {
          const updateData = {
            name: values.queryName,
            description: values.description || '',
            query: sqlQuery,
            visualizationType: visualizationType,
            visualizationConfig: getCurrentConfig()
          };
          await axiosInstance.put(`/query/${existingId}`, updateData);
          // Update currentQueryId so future saves will use PUT
          setCurrentQueryId(existingId);
          message.success('Query updated successfully');
          setSaveModalVisible(false);
          return;
        } catch (updateErr) {
          console.error('Error updating existing query:', updateErr);
          message.error(updateErr?.response?.data?.message || 'Failed to update existing query');
          return;
        }
      }
      message.error(error?.response?.data?.message || 'Failed to save query');
    } finally {
      setSaving(false);
    }
  };

  // Load saved query
  const handleLoadQuery = (savedQuery) => {
    setQueryName(savedQuery.name);
    setSqlQuery(savedQuery.sqlQuery);
    setVisualizationType(savedQuery.visualizationType || 'Table');
    // Track the current query ID for updates
    setCurrentQueryId(savedQuery._id || savedQuery.id);
    
    // Load visualization config if available
    if (savedQuery.visualizationConfig) {
      const newConfigs = {
        ...visualizationConfigs,
        [savedQuery.visualizationType || 'Table']: savedQuery.visualizationConfig
      };
      setVisualizationConfigs(newConfigs);
      // Also update localStorage to persist the loaded configuration
      localStorage.setItem('visualizationConfigs', JSON.stringify(newConfigs));
    }
    
    message.success(`Loaded query: ${savedQuery.name}`);
  };

  // Format cell values for display
  const formatCellValue = (value) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  // Render visualization
  const renderVisualization = () => {
    if (!queryResults || !queryResults.data || queryResults.data.length === 0) {
      return (
        <div className="flex items-center justify-center h-64" style={{ color: styles.textSecondary }}>
          <div className="text-center">
            <div className="text-lg font-medium mb-2" style={{ fontFamily: styles.headingFont, color: brandColor, fontWeight: 500 }}>No data to visualize</div>
            <div className="text-sm" style={{ fontFamily: styles.bodyFont, color: styles.textSecondary }}>Execute a query to see results</div>
          </div>
        </div>
      );
    }

    // Get data and columns based on whether this is a batch or single query
    let data, columns;
    
    if (queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries.length > 0) {
      // Use current batch query data
      const currentQuery = queryResults.batchQueries[currentBatchQueryIndex];
      if (currentQuery) {
        data = currentQuery.data;
        columns = currentQuery.columns;
      } else {
        // Fallback to first query if index is invalid
        data = queryResults.batchQueries[0].data;
        columns = queryResults.batchQueries[0].columns;
      }
    } else {
      // Single query or fallback
      data = queryResults.data;
      columns = queryResults.columns;
    }

    switch (visualizationType) {
      case 'Line Chart':
        return (
          <EnhancedVisualization
            type="Line Chart"
            data={data}
            columns={columns}
            config={getCurrentConfig()}
            height={450}
          />
        );

      case 'Bar Chart':
        return (
          <EnhancedVisualization
            type="Bar Chart"
            data={data}
            columns={columns}
            config={getCurrentConfig()}
            height={450}
          />
        );

      case 'Pie Chart':
        return (
          <EnhancedVisualization
            type="Pie Chart"
            data={data}
            columns={columns}
            config={getCurrentConfig()}
            height={450}
          />
        );

      case 'KPI':
        return (
          <EnhancedVisualization
            type="KPI"
            data={data}
            columns={columns}
            config={getCurrentConfig()}
            height={450}
          />
        );

       case 'Table':
         return (
           <EnhancedVisualization
             type="Table"
             data={data}
             columns={columns}
             config={getCurrentConfig()}
             height={450}
           />
         );

       default:
         return (
           <>
             <style>
               {`
                 .query-studio-table-wrapper .ant-table-wrapper {
                   border-radius: 0 !important;
                 }
                 .query-studio-table-wrapper .ant-table {
                   border-radius: 0 !important;
                   border: 0.5px solid #E5E7EB !important;
                 }
                 .query-studio-table-wrapper .ant-table-container {
                   border-radius: 0 !important;
                   border: 0.5px solid #E5E7EB !important;
                 }
                 .query-studio-table-wrapper .ant-table-thead > tr > th {
                   border: 0.5px solid #E5E7EB !important;
                 }
                 .query-studio-table-wrapper .ant-table-tbody > tr > td {
                   border: 0.5px solid #E5E7EB !important;
                 }
                 .query-studio-table-wrapper .ant-table-thead > tr > th:first-child {
                   border-top-left-radius: 0 !important;
                 }
                 .query-studio-table-wrapper .ant-table-thead > tr > th:last-child {
                   border-top-right-radius: 0 !important;
                 }
                 .query-studio-table-wrapper .ant-table-tbody > tr:last-child > td:first-child {
                   border-bottom-left-radius: 0 !important;
                 }
                 .query-studio-table-wrapper .ant-table-tbody > tr:last-child > td:last-child {
                   border-bottom-right-radius: 0 !important;
                 }
               `}
             </style>
             <div className="query-studio-table-wrapper">
               <Table
                 dataSource={data}
                rowKey={(row) => row.id || row._id || row.key || JSON.stringify(row)}
                 columns={columns.map(col => ({
                   title: col,
                   dataIndex: col,
                   key: col,
                   render: (value) => (
                     <span title={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}>
                       {formatCellValue(value)}
                     </span>
                   ),
                   ellipsis: true
                 }))}
                 pagination={{ 
                   pageSize: 10,
                   showSizeChanger: true,
                   showQuickJumper: true,
                   showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                 }}
                 scroll={{ x: true, y: 400 }}
                 size="small"
               />
             </div>
           </>
         );
    }
  };

  return (
    <div className="flex flex-1 overflow-y-auto overflow-x-hidden query-studio-container" style={{ 
      fontFamily: styles.bodyFont,
      backgroundColor: styles.primaryBackground,
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Query Studio Content */}
      <div className="flex flex-col overflow-y-auto overflow-x-hidden px-6 py-5" style={{ 
        backgroundColor: styles.primaryBackground,
        minWidth: 0,
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div className="flex-1 space-y-4" style={{ maxWidth: '100%', width: '100%', minWidth: 0 }}>
          {/* Query Configuration */}
          <Card 
            title={
              <span style={{ color: brandColor, fontFamily: styles.headingFont, fontWeight: 600 }}>Configuration</span>
            } 
            className="flex-shrink-0"
            style={{ 
              backgroundColor: styles.cardBg,
              borderColor: styles.borderColor,
              borderWidth: '1px',
              boxShadow: 'none',
              borderRadius: 0,
              fontFamily: styles.headingFont,
              fontWeight: 500,
              maxWidth: '100%',
              width: '100%',
              overflow: 'visible'
            }}
            headStyle={{ borderBottom: '1px solid #E5E7EB', padding: '16px 24px' }}
            styles={{ body: { padding: '24px', overflow: 'visible' } }}
          >
            <div className="flex flex-col lg:flex-row gap-4" style={{ width: '100%', minWidth: 0 }}>
              <div className="border-r border-gray-200 pr-4" style={{ minWidth: 0, flex: hasTableRun ? '1 1 25%' : '1 1 45%' }}>
                <Text strong className="text-sm" style={{ 
                  color: brandColor,
                  fontFamily: styles.bodyFont,
                  fontWeight: 500
                }}>Query Name</Text>
                <Input
                  placeholder="e.g., Weekly User Signups"
                  value={queryName}
                  onChange={(e) => setQueryName(e.target.value)}
                  className="mt-1"
                  size="small"
                  style={{ 
                    borderColor: styles.borderColor,
                    borderRadius: 0,
                    fontFamily: styles.bodyFont,
                    fontWeight: 400,
                    fontSize: '14px',
                    color: styles.textPrimary,
                    width: '100%'
                  }}
                />
              </div>
 
              <div className={hasTableRun ? "border-r border-gray-200 pr-4 pl-4" : "pl-4"} style={{ minWidth: 0, flex: hasTableRun ? '0 0 auto' : '1 1 45%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: '20px' }}></div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="primary"
                    icon={<Play size={14} />}
                    onClick={executeQuery}
                    loading={executing}
                    className="transition-all duration-200 hover:opacity-90"
                    style={{ 
                      backgroundColor: brandColor, 
                      borderColor: brandColor,
                      border: 'none',
                      color: '#ffffff',
                      fontFamily: styles.headingFont,
                      fontWeight: 500,
                      fontSize: '13px',
                      borderRadius: '0px',
                      padding: '6px 12px',
                      height: '28px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      flexShrink: 0
                    }}
                  >
                    Run
                  </Button>
                  <Button
                    icon={<Save size={14} />}
                    onClick={() => setSaveModalVisible(true)}
                    disabled={queryResults && queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries.length > 1}
                    className="transition-all duration-200 hover:opacity-90"
                    style={{ 
                      color: brandColor,
                      borderColor: brandColor,
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      backgroundColor: '#ffffff',
                      fontFamily: styles.headingFont,
                      fontWeight: 500,
                      fontSize: '13px',
                      borderRadius: '0px',
                      padding: '6px 12px',
                      height: '28px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      flexShrink: 0
                    }}
                    title={queryResults && queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries.length > 1 ? 
                      "Cannot save batch queries with multiple tables. Use 'Save This Table' button in results section." : 
                      "Save query"
                    }
                  >
                    Save
                  </Button>
                </div>
              </div>

              {/* Visualization controls - always reserve space to prevent layout jump */}
              <div className="pl-4" style={{ minWidth: 0, flex: '1 1 35%', overflow: 'hidden' }}>
                {hasTableRun ? (
                  <>
                    <Text strong className="text-sm" style={{ 
                      color: brandColor,
                      fontFamily: styles.bodyFont,
                      fontWeight: 500
                    }}>Visualization Type</Text>
                    <div className="flex items-center gap-2 mt-1" style={{ width: '100%', minWidth: 0 }}>
                      <Select
                        value={visualizationType}
                        onChange={handleVisualizationTypeChange}
                        size="small"
                        disabled={isBatchWithMultipleResults()}
                        options={visualizationOptions}
                        style={{ 
                          borderColor: styles.borderColor,
                          borderRadius: 0,
                          fontFamily: styles.bodyFont,
                          fontWeight: 400,
                          fontSize: '14px',
                          color: styles.textPrimary,
                          minWidth: 0,
                          width: '100%'
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div /> // Empty placeholder to reserve space
                )}
              </div>
            </div>
          </Card>

          {/* SQL Editor */}
          <Card 
            title={
              <div className="flex items-center justify-between w-full">
                <span style={{ color: brandColor, fontFamily: styles.headingFont, fontWeight: 600 }}>SQL Editor</span>
                <Button
                  type="primary"
                  icon={<Book size={14} />}
                  onClick={() => {
                    // Open docs - you can customize this action
                    window.open('https://docs.example.com', '_blank');
                  }}
                  className="transition-all duration-200 hover:opacity-90"
                  style={{ 
                    backgroundColor: brandColor, 
                    borderColor: brandColor,
                    border: 'none',
                    color: '#ffffff',
                    fontFamily: styles.headingFont,
                    fontWeight: 500,
                    fontSize: '13px',
                    borderRadius: '0px',
                    padding: '6px 12px',
                    height: '28px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    flexShrink: 0
                  }}
                >
                  Docs
                </Button>
              </div>
            } 
            className="flex-shrink-0"
            style={{ 
              backgroundColor: styles.cardBg,
              borderColor: styles.borderColor,
              borderWidth: '1px',
              boxShadow: 'none',
              borderRadius: 0,
              fontFamily: styles.headingFont,
              fontWeight: 500,
              maxWidth: '100%',
              width: '100%',
              overflow: 'hidden'
            }}
            headStyle={{ borderBottom: '1px solid #E5E7EB', padding: '16px 24px' }}
            styles={{ body: { padding: '24px' } }}
          >
            <div 
              className="sql-editor-container monaco-editor-container"
              style={{ 
                height: '360px', 
                border: '1px solid #E5E7EB', 
                borderRadius: 0,
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#ffffff',
                maxWidth: '100%',
                width: '100%'
              }}
            >
              <MonacoEditor
                height="360px"
                width="100%"
                language="sql"
                value={sqlQuery}
                onChange={setSqlQuery}
                theme="vs-light"
                options={{
                  selectOnLineNumbers: true,
                  roundedSelection: false,
                  readOnly: false,
                  cursorStyle: 'line',
                  automaticLayout: false, // Disable to prevent ResizeObserver issues
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  folding: true,
                  lineDecorationsWidth: 10,
                  lineNumbersMinChars: 3,
                  renderLineHighlight: 'line',
                  scrollbar: {
                    vertical: 'auto',
                    horizontal: 'auto',
                    verticalScrollbarSize: 8,
                    horizontalScrollbarSize: 8
                  },
                  padding: {
                    top: 10,
                    bottom: 10
                  },
                  // Additional options to prevent ResizeObserver issues
                  fixedOverflowWidgets: true,
                  disableLayerHinting: true,
                  renderWhitespace: 'none',
                  renderControlCharacters: false,
                  smoothScrolling: false,
                  cursorBlinking: 'blink',
                  cursorSmoothCaretAnimation: false
                }}
                onMount={(editor) => {
                  setEditorMounted(true);
                  
                  // Ensure the editor is properly initialized
                  editor.focus();
                  
                  // Manual layout management to prevent ResizeObserver issues
                  let layoutTimeout;
                  let isLayoutScheduled = false;
                  
                  const scheduleLayout = () => {
                    if (!isLayoutScheduled) {
                      isLayoutScheduled = true;
                      layoutTimeout = setTimeout(() => {
                        try {
                          editor.layout();
                        } catch (error) {
                          // Ignore ResizeObserver errors
                          if (!error.message?.includes('ResizeObserver')) {
                            console.error('Layout error:', error);
                          }
                        } finally {
                          isLayoutScheduled = false;
                        }
                      }, 16); // Use requestAnimationFrame timing
                    }
                  };
                  
                  // Debounced resize handler
                  const handleResize = () => {
                    scheduleLayout();
                  };
                  
                  // Use ResizeObserver with proper error handling
                  let resizeObserver;
                  try {
                    resizeObserver = new ResizeObserver((entries) => {
                      // Use requestAnimationFrame to defer layout updates
                      requestAnimationFrame(() => {
                        scheduleLayout();
                      });
                    });
                    
                    const container = editor.getDomNode();
                    if (container) {
                      resizeObserver.observe(container);
                    }
                  } catch (error) {
                    console.warn('ResizeObserver not supported, using window resize event');
                    window.addEventListener('resize', handleResize);
                  }
                  
                  // Cleanup function
                  return () => {
                    if (layoutTimeout) {
                      clearTimeout(layoutTimeout);
                    }
                    if (resizeObserver) {
                      resizeObserver.disconnect();
                    } else {
                      window.removeEventListener('resize', handleResize);
                    }
                  };
                }}
                loading={<div style={{ 
                  height: '300px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  color: styles.textSecondary
                }}>Loading SQL Editor...</div>}
              />
              
              {/* Loading overlay for query loading */}
              {loadingQuery && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 0
                  }}
                >
                  <Spin size="large" />
                  <span style={{ marginLeft: '12px', color: styles.textSecondary, fontFamily: styles.bodyFont }}>
                    Loading query...
                  </span>
                </div>
              )}
              
              {/* Fallback textarea in case MonacoEditor fails */}
              {!editorMounted && (
                <textarea
                  value={sqlQuery}
                  onChange={(e) => setSqlQuery(e.target.value)}
                  placeholder="-- Write your SQL query here&#10;-- Example queries:&#10;SELECT * FROM sessions LIMIT 10;&#10;SELECT * FROM analytics LIMIT 5;&#10;SELECT sessionId, userId, duration FROM sessions LIMIT 10;"
                  style={{
                    width: '100%',
                    height: '300px',
                    border: 'none',
                    outline: 'none',
                    padding: '10px',
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    resize: 'none',
                    backgroundColor: '#ffffff',
                    color: styles.textPrimary,
                    borderRadius: 0,
                    border: '1px solid #E5E7EB'
                  }}
                />
              )}
            </div>
          </Card>


          {/* Batch Query Warning Banner */}
          {queryResults && queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries.length > 1 && (
            <Card 
              style={{ 
                backgroundColor: '#fff3cd',
                borderColor: brandColor,
                borderWidth: '1px',
                marginBottom: '16px',
                borderRadius: 0,
                boxShadow: 'none'
              }}
              styles={{ body: { padding: '16px 24px' } }}
            >
              <div className="flex items-center gap-3">
                <div style={{ color: brandColor, fontSize: '16px' }}>⚠️</div>
                <div className="flex-1">
                  <div style={{ color: brandColor, fontWeight: 600, fontSize: '14px', marginBottom: '4px', fontFamily: styles.headingFont }}>
                    Batch Query Detected
                  </div>
                  <div style={{ color: brandColor, fontSize: '13px', fontFamily: styles.bodyFont }}>
                    You have {queryResults.batchQueries.length} result tables. You can only save and configure visualizations for <strong>one table at a time</strong>. 
                    Use the navigation buttons to select which table you want to save, then modify your query to return only that result set.
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Results */}
           {queryResults && (
             <Card 
               title={
                 <div className="flex items-center justify-between">
                   <div className="flex items-center gap-4">
                     <div className="flex flex-col">
                       <span className="text-sm" style={{ color: brandColor, fontFamily: styles.headingFont, fontWeight: 600 }}>
                       {queryResults.isBatch ? (
                         <>
                           Query {currentBatchQueryIndex + 1} Results ({queryResults.batchQueries[currentBatchQueryIndex]?.count || 0} rows)
                           <span style={{ color: styles.textSecondary, marginLeft: '8px', fontFamily: styles.bodyFont }}>
                             - Batch: {queryResults.batchInfo.queriesWithResults}/{queryResults.batchInfo.totalQueries} queries with results
                           </span>
                         </>
                       ) : (
                         <>
                           Results ({queryResults.data.length} rows)
                           {queryResults.batchInfo && (
                             <span style={{ color: styles.textSecondary, marginLeft: '8px', fontFamily: styles.bodyFont }}>
                               - Batch: {queryResults.batchInfo.successfulQueries}/{queryResults.batchInfo.totalQueries} queries
                             </span>
                           )}
                         </>
                       )}
                       </span>
                       {/* Show current query preview for batch */}
                       {queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries[currentBatchQueryIndex] && (
                         <span style={{ 
                           fontSize: '11px', 
                           color: styles.textSecondary, 
                           fontFamily: 'Monaco, monospace',
                           marginTop: '2px',
                           maxWidth: '400px',
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap'
                         }}>
                           {queryResults.batchQueries[currentBatchQueryIndex].query}
                         </span>
                       )}
                     </div>
                     
                     {/* Batch Query Navigation */}
                     {queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries.length > 1 && (
                       <div className="flex items-center gap-2">
                         <Button
                           size="small"
                           onClick={() => setCurrentBatchQueryIndex(Math.max(0, currentBatchQueryIndex - 1))}
                           disabled={currentBatchQueryIndex === 0}
                           className="transition-all duration-200 hover:opacity-90"
                           style={{ 
                             color: brandColor,
                             borderColor: brandColor,
                             borderWidth: '1px',
                             borderStyle: 'solid',
                             backgroundColor: '#ffffff',
                             fontFamily: styles.headingFont,
                             fontWeight: 500,
                             fontSize: '13px',
                             borderRadius: '0px',
                             padding: '6px 12px',
                             height: '28px',
                             display: 'inline-flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             gap: '4px'
                           }}
                         >
                           ← Previous
                         </Button>
                         <span style={{ color: styles.textSecondary, fontSize: '12px', fontFamily: styles.bodyFont }}>
                           {currentBatchQueryIndex + 1} of {queryResults.batchQueries.length}
                         </span>
                         <Button
                           size="small"
                           onClick={() => setCurrentBatchQueryIndex(Math.min(queryResults.batchQueries.length - 1, currentBatchQueryIndex + 1))}
                           disabled={currentBatchQueryIndex === queryResults.batchQueries.length - 1}
                           className="transition-all duration-200 hover:opacity-90"
                           style={{ 
                             color: brandColor,
                             borderColor: brandColor,
                             borderWidth: '1px',
                             borderStyle: 'solid',
                             backgroundColor: '#ffffff',
                             fontFamily: styles.headingFont,
                             fontWeight: 500,
                             fontSize: '13px',
                             borderRadius: '0px',
                             padding: '6px 12px',
                             height: '28px',
                             display: 'inline-flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             gap: '4px'
                           }}
                         >
                           Next →
                         </Button>
                       </div>
                     )}
                   </div>
                   <div className="flex items-center gap-2">
                     {/* Save Current Table Button for Batch Queries */}
                     {queryResults.isBatch && queryResults.batchQueries && queryResults.batchQueries.length > 1 && (
                         <Button
                           icon={<Save size={14} />}
                           onClick={() => {
                             // Extract the current query from the batch
                             const currentQuery = queryResults.batchQueries[currentBatchQueryIndex];
                             if (currentQuery) {
                               // Update the SQL query to only include the current query
                               setSqlQuery(currentQuery.query);
                               // Reset to single query mode
                               setQueryResults({
                                 data: currentQuery.data,
                                 columns: currentQuery.columns
                               });
                               message.success(`Switched to single query mode. You can now save this table.`);
                             }
                           }}
                           size="small"
                           className="transition-all duration-200 hover:opacity-90"
                           style={{ 
                             color: brandColor,
                             borderColor: brandColor,
                             borderWidth: '1px',
                             borderStyle: 'solid',
                             backgroundColor: '#ffffff',
                             fontFamily: styles.headingFont,
                             fontWeight: 500,
                             fontSize: '13px',
                             borderRadius: '0px',
                             padding: '6px 12px',
                             height: '28px',
                             display: 'inline-flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             gap: '4px'
                           }}
                         >
                           Save This Table
                         </Button>
                     )}
                     {isTableConfigAvailable() && (
                       <Button
                         icon={<Settings size={14} />}
                         onClick={() => setConfigModalVisible(true)}
                         className="transition-all duration-200 hover:opacity-90"
                         style={{ 
                           color: brandColor,
                           borderColor: brandColor,
                           borderWidth: '1px',
                           borderStyle: 'solid',
                           backgroundColor: '#ffffff',
                           fontFamily: styles.headingFont,
                           fontWeight: 500,
                           fontSize: '13px',
                           borderRadius: '0px',
                           padding: '6px 12px',
                           height: '28px',
                           display: 'inline-flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           gap: '4px'
                         }}
                       >
                         Configure Table
                       </Button>
                     )}
                     {isConfigAvailable() && !isBatchWithMultipleResults() && (
                       <Button
                         icon={<Settings size={14} />}
                         onClick={() => setConfigModalVisible(true)}
                         className="transition-all duration-200 hover:opacity-90"
                         style={{ 
                           color: brandColor,
                           borderColor: brandColor,
                           borderWidth: '1px',
                           borderStyle: 'solid',
                           backgroundColor: '#ffffff',
                           fontFamily: styles.headingFont,
                           fontWeight: 500,
                           fontSize: '13px',
                           borderRadius: '0px',
                           padding: '6px 12px',
                           height: '28px',
                           display: 'inline-flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           gap: '4px'
                         }}
                       >
                         Configure {visualizationType}
                       </Button>
                     )}
                   </div>
                 </div>
               }
               className=""
               style={{ 
                 backgroundColor: styles.cardBg,
                 borderColor: styles.borderColor,
                 borderWidth: '1px',
                 boxShadow: 'none',
                 borderRadius: 0,
                 fontFamily: styles.headingFont,
                 fontWeight: 500,
                 width: '100%',
                 maxWidth: '100%',
                 minWidth: 0,
                 overflow: 'visible',
                 flex: 'none'
               }}
               headStyle={{ borderBottom: '1px solid #E5E7EB', padding: '16px 24px' }}
               styles={{ body: { padding: visualizationType === 'KPI' ? '0px' : '24px', width: '100%', maxWidth: '100%', overflow: 'visible', minWidth: 0 } }}
             >
               {renderVisualization()}
             </Card>
           )}
        </div>
      </div>


      {/* Visualization Configuration Modal */}
      <VisualizationConfigModal
        visible={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
        visualizationType={visualizationType}
        config={getCurrentConfig()}
        onConfigChange={saveVisualizationConfig}
        availableColumns={queryResults?.columns || []}
        previewData={queryResults?.data ? queryResults.data.slice(0, 5) : []}
      />

      {/* Save Query Modal */}
      <SaveQueryModal
        visible={saveModalVisible}
        onClose={() => setSaveModalVisible(false)}
        onSave={handleSaveQuery}
        loading={saving}
        initialValues={{
          queryName: queryName || '',
          description: ''
        }}
      />
    </div>
  );
};

export default QueryStudioContent;
